import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  Avatar,
  Fade,
  Stack,
  alpha,
  useTheme,
  useMediaQuery,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  BugReport,
  ExpandMore,
  Warning,
  CheckCircle,
  Science,
  Security,
  Assessment,
  PlayArrow,
  Refresh
} from '@mui/icons-material';
import axios from 'axios';
import { phiTestCases } from '../data/phiTestCases';
import toast from 'react-hot-toast';

const ComplianceTester: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [testPrompt, setTestPrompt] = useState('');
  const [selectedTest, setSelectedTest] = useState('');
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [framework, setFramework] = useState('HIPAA');

  const handleTest = async () => {
    if (!testPrompt.trim()) {
      toast.error('Please enter a prompt to test');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/prompts/analyze', {
        prompt: testPrompt
      });

      // Transform the response to match our expected format
      const analysisData = response.data.analysis || {};
      const complianceData = analysisData.compliance || {};

      const transformedResult = {
        compliance_score: complianceData.phi_detected
          ? Math.max(0, 100 - complianceData.phi_confidence * 100)
          : 100,
        risk_level: complianceData.phi_detected
          ? complianceData.phi_confidence > 0.7
            ? 'critical'
            : complianceData.phi_confidence > 0.5
              ? 'high'
              : 'medium'
          : 'low',
        detected_phi:
          complianceData.phi_categories?.map((category: string, idx: number) => ({
            type: category,
            value: 'Detected in text',
            confidence: complianceData.phi_confidence || 0,
            position: { start: 0, end: testPrompt.length }
          })) || [],
        violations: complianceData.phi_detected
          ? [`PHI detected: ${complianceData.phi_categories?.join(', ')}`]
          : [],
        recommended_action: complianceData.phi_detected
          ? 'Redact or encrypt PHI before processing'
          : 'Prompt is compliant',
        framework: framework
      };

      setResult(transformedResult);

      if (transformedResult.detected_phi?.length > 0) {
        toast.error(`PHI Detected: ${transformedResult.detected_phi.length} categories found`);
      } else {
        toast.success('No PHI detected - Prompt is compliant');
      }
    } catch (error: any) {
      console.error('Test failed:', error);
      toast.error(error.response?.data?.message || 'Failed to analyze prompt');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const loadTestCase = (testCase: any) => {
    setTestPrompt(testCase.prompt);
    setSelectedTest(testCase.id);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Enhanced Header with Gradient Background */}
      <Fade in timeout={600}>
        <Box
          sx={{
            mb: 4,
            p: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '300px',
              height: '300px',
              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
              borderRadius: '50%',
              transform: 'translate(30%, -30%)'
            }
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={3}
          >
            <Box display="flex" alignItems="center" gap={2.5}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`
                }}
              >
                <Science sx={{ fontSize: 32, color: 'white' }} />
              </Avatar>
              <Box>
                <Typography
                  variant={isMobile ? 'h5' : 'h4'}
                  fontWeight={700}
                  sx={{
                    mb: 0.5,
                    background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.primary.main} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  HIPAA Compliance & PHI Detection Tester
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Security sx={{ fontSize: 16 }} />
                  Test prompts for Protected Health Information (PHI) and HIPAA compliance
                  violations
                </Typography>
              </Box>
            </Box>

            <Tooltip title="Reset Test">
              <IconButton
                onClick={() => {
                  setTestPrompt('');
                  setSelectedTest('');
                  setResult(null);
                }}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                    transform: 'rotate(180deg)'
                  },
                  transition: 'all 0.5s ease'
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Fade>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Fade in timeout={800}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  p: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.dark, 0.05)} 100%)`,
                  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
                }}
              >
                <Typography variant="h6" fontWeight={700}>
                  PHI Detection Test
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure and run compliance tests
                </Typography>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'background.paper',
                          '&:hover': {
                            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                          }
                        }
                      }}
                    >
                      <InputLabel>Compliance Framework</InputLabel>
                      <Select
                        value={framework}
                        onChange={(e) => setFramework(e.target.value)}
                        label="Compliance Framework"
                      >
                        <MenuItem value="HIPAA">
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Box
                              sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444' }}
                            />
                            HIPAA
                          </Box>
                        </MenuItem>
                        <MenuItem value="GDPR">
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Box
                              sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f59e0b' }}
                            />
                            GDPR
                          </Box>
                        </MenuItem>
                        <MenuItem value="SOC2">
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Box
                              sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#3b82f6' }}
                            />
                            SOC 2
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'background.paper',
                          '&:hover': {
                            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                          }
                        }
                      }}
                    >
                      <InputLabel>Load Test Case</InputLabel>
                      <Select
                        value={selectedTest}
                        onChange={(e) => {
                          const tc = phiTestCases.find((t) => t.id === e.target.value);
                          if (tc) loadTestCase(tc);
                        }}
                        label="Load Test Case"
                      >
                        {phiTestCases.map((tc) => (
                          <MenuItem key={tc.id} value={tc.id}>
                            {tc.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label="Test Prompt"
                  value={testPrompt}
                  onChange={(e) => setTestPrompt(e.target.value)}
                  placeholder="Enter a prompt to test for PHI and compliance violations..."
                  sx={{
                    mt: 3,
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      '&:hover': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                      }
                    }
                  }}
                />

                <Button
                  variant="contained"
                  startIcon={loading ? <Assessment /> : <PlayArrow />}
                  onClick={handleTest}
                  disabled={!testPrompt || loading}
                  fullWidth
                  size="large"
                  sx={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                    },
                    '&:disabled': {
                      background: alpha(theme.palette.action.disabled, 0.12)
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {loading ? 'Analyzing...' : 'Test PHI Detection'}
                </Button>

                {result && (
                  <Fade in timeout={600}>
                    <Box mt={4}>
                      <Divider sx={{ mb: 3 }} />

                      {/* Enhanced Overall Compliance Score */}
                      <Card
                        variant="outlined"
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          border: `2px solid ${alpha(
                            result.compliance_score >= 80
                              ? theme.palette.success.main
                              : result.compliance_score >= 50
                                ? theme.palette.warning.main
                                : theme.palette.error.main,
                            0.3
                          )}`,
                          background: `linear-gradient(135deg, ${alpha(
                            result.compliance_score >= 80
                              ? theme.palette.success.main
                              : result.compliance_score >= 50
                                ? theme.palette.warning.main
                                : theme.palette.error.main,
                            0.05
                          )} 0%, transparent 100%)`
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={2}
                          >
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 1.5,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: `linear-gradient(135deg, ${
                                    result.compliance_score >= 80
                                      ? theme.palette.success.main
                                      : result.compliance_score >= 50
                                        ? theme.palette.warning.main
                                        : theme.palette.error.main
                                  } 0%, ${alpha(
                                    result.compliance_score >= 80
                                      ? theme.palette.success.main
                                      : result.compliance_score >= 50
                                        ? theme.palette.warning.main
                                        : theme.palette.error.main,
                                    0.8
                                  )} 100%)`,
                                  boxShadow: `0 4px 12px ${alpha(
                                    result.compliance_score >= 80
                                      ? theme.palette.success.main
                                      : result.compliance_score >= 50
                                        ? theme.palette.warning.main
                                        : theme.palette.error.main,
                                    0.3
                                  )}`
                                }}
                              >
                                <Assessment sx={{ color: 'white', fontSize: 20 }} />
                              </Box>
                              <Typography variant="h6" fontWeight={700}>
                                Compliance Score
                              </Typography>
                            </Box>
                            <Chip
                              label={`${(result.compliance_score || 0).toFixed(1)}%`}
                              color={
                                result.compliance_score >= 80
                                  ? 'success'
                                  : result.compliance_score >= 50
                                    ? 'warning'
                                    : 'error'
                              }
                              sx={{
                                fontWeight: 700,
                                fontSize: '1rem',
                                height: 36,
                                px: 2
                              }}
                            />
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={result.compliance_score || 0}
                            color={
                              result.compliance_score >= 80
                                ? 'success'
                                : result.compliance_score >= 50
                                  ? 'warning'
                                  : 'error'
                            }
                            sx={{
                              height: 12,
                              borderRadius: 2,
                              bgcolor: alpha(theme.palette.action.disabled, 0.1)
                            }}
                          />
                          <Box display="flex" alignItems="center" mt={2.5}>
                            {result.risk_level === 'high' || result.risk_level === 'critical' ? (
                              <Warning sx={{ color: 'error.main', mr: 1.5, fontSize: 24 }} />
                            ) : (
                              <CheckCircle sx={{ color: 'success.main', mr: 1.5, fontSize: 24 }} />
                            )}
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Risk Level
                              </Typography>
                              <Typography variant="body1" fontWeight={700}>
                                {result.risk_level?.toUpperCase() || 'UNKNOWN'}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>

                      {/* Enhanced Detected PHI */}
                      {result.detected_phi && result.detected_phi.length > 0 && (
                        <Accordion
                          defaultExpanded
                          sx={{
                            mb: 2,
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                            '&:before': { display: 'none' },
                            boxShadow: 'none'
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMore />}
                            sx={{
                              bgcolor: alpha(theme.palette.error.main, 0.05),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.error.main, 0.08)
                              }
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <Warning sx={{ color: 'error.main' }} />
                              <Typography variant="h6" fontWeight={700}>
                                Detected PHI
                              </Typography>
                              <Chip
                                label={result.detected_phi.length}
                                size="small"
                                color="error"
                                sx={{ fontWeight: 700 }}
                              />
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ p: 0 }}>
                            <List>
                              {result.detected_phi.map((phi: any, idx: number) => (
                                <ListItem
                                  key={idx}
                                  divider={idx < result.detected_phi.length - 1}
                                  sx={{
                                    py: 2,
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.error.main, 0.02)
                                    }
                                  }}
                                >
                                  <ListItemText
                                    primary={
                                      <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                                        <Chip
                                          label={phi.type}
                                          size="small"
                                          color="error"
                                          sx={{ fontWeight: 600 }}
                                        />
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontFamily: 'monospace',
                                            bgcolor: alpha(theme.palette.error.main, 0.08),
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: 1,
                                            fontWeight: 500
                                          }}
                                        >
                                          "{phi.value}"
                                        </Typography>
                                      </Box>
                                    }
                                    secondary={
                                      <Stack direction="row" spacing={2} mt={1}>
                                        <Box>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            fontWeight={600}
                                          >
                                            Confidence
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            fontWeight={600}
                                            color="error.main"
                                          >
                                            {(phi.confidence * 100).toFixed(1)}%
                                          </Typography>
                                        </Box>
                                        <Box>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            fontWeight={600}
                                          >
                                            Position
                                          </Typography>
                                          <Typography variant="body2" fontWeight={500}>
                                            {phi.position.start} - {phi.position.end}
                                          </Typography>
                                        </Box>
                                      </Stack>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </AccordionDetails>
                        </Accordion>
                      )}

                      {/* Enhanced Violations */}
                      {result.violations && result.violations.length > 0 && (
                        <Accordion
                          sx={{
                            mb: 2,
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                            '&:before': { display: 'none' },
                            boxShadow: 'none'
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMore />}
                            sx={{
                              bgcolor: alpha(theme.palette.warning.main, 0.05),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.warning.main, 0.08)
                              }
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <BugReport sx={{ color: 'warning.main' }} />
                              <Typography variant="h6" fontWeight={700}>
                                Compliance Violations
                              </Typography>
                              <Chip
                                label={result.violations.length}
                                size="small"
                                color="warning"
                                sx={{ fontWeight: 700 }}
                              />
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ p: 0 }}>
                            <List>
                              {result.violations.map((violation: string, idx: number) => (
                                <ListItem
                                  key={idx}
                                  divider={idx < result.violations.length - 1}
                                  sx={{
                                    py: 2,
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.warning.main, 0.02)
                                    }
                                  }}
                                >
                                  <ListItemText
                                    primary={
                                      <Box display="flex" alignItems="flex-start" gap={1.5}>
                                        <Warning
                                          sx={{ color: 'warning.main', fontSize: 20, mt: 0.25 }}
                                        />
                                        <Typography variant="body2" fontWeight={500}>
                                          {violation}
                                        </Typography>
                                      </Box>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </AccordionDetails>
                        </Accordion>
                      )}

                      {/* Enhanced Recommendations */}
                      {result.recommended_action && (
                        <Alert
                          severity="info"
                          sx={{
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`
                          }}
                        >
                          <Typography variant="subtitle2" gutterBottom fontWeight={700}>
                            Recommended Action:
                          </Typography>
                          <Typography variant="body2">{result.recommended_action}</Typography>
                        </Alert>
                      )}
                    </Box>
                  </Fade>
                )}
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        <Grid item xs={12} md={4}>
          <Fade in timeout={1000}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                overflow: 'hidden',
                position: 'sticky',
                top: 24
              }}
            >
              <Box
                sx={{
                  p: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.dark, 0.05)} 100%)`,
                  borderBottom: `2px solid ${alpha(theme.palette.secondary.main, 0.1)}`
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.3)}`
                    }}
                  >
                    <BugReport sx={{ color: 'white', fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" fontWeight={700}>
                    Test Cases Library
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Click to load realistic medical scenarios
                </Typography>
              </Box>
              <CardContent sx={{ p: 0 }}>
                <List sx={{ maxHeight: 600, overflow: 'auto' }}>
                  {phiTestCases.map((tc, index) => (
                    <React.Fragment key={tc.id}>
                      <ListItemButton
                        onClick={() => loadTestCase(tc)}
                        selected={selectedTest === tc.id}
                        sx={{
                          py: 2,
                          px: 3,
                          transition: 'all 0.2s ease',
                          borderLeft: `3px solid transparent`,
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                            borderLeft: `3px solid ${theme.palette.primary.main}`
                          },
                          '&.Mui-selected': {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            borderLeft: `3px solid ${theme.palette.primary.main}`,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.12)
                            }
                          }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight={600} gutterBottom>
                              {tc.name}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                              >
                                {tc.description}
                              </Typography>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                <Chip
                                  label={tc.severity.toUpperCase()}
                                  size="small"
                                  color={getSeverityColor(tc.severity) as any}
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '0.688rem',
                                    height: 22
                                  }}
                                />
                                {tc.expectedDetections.length > 0 && (
                                  <Chip
                                    label={`${tc.expectedDetections.length} PHI types`}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: '0.688rem',
                                      height: 22
                                    }}
                                  />
                                )}
                              </Stack>
                            </Box>
                          }
                        />
                      </ListItemButton>
                      {index < phiTestCases.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Fade>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ComplianceTester;
